/**
 * Mirror of `gann-online-pos-backend/src/website/filter-config/constants/filter-catalog.ts`.
 * If you change the backend catalog, update this file too — a sync script
 * (scripts/sync-filter-catalog.ts) is planned. Backend remains source of truth.
 */

import { ItemType } from '../../inventory/type/item'

export enum FilterKind {
  General = 'General',
  Specific = 'Specific',
}

export interface FilterDescriptor {
  key: string
  label: string
  kind: FilterKind
  allowedTypes: ReadonlyArray<ItemType>
}

const ALL_TYPES: ReadonlyArray<ItemType> = [
  ItemType.Jewelery,
  ItemType.Watch,
  ItemType.Tableware,
  ItemType.Giftware,
]

export const FILTER_CATALOG: Record<string, FilterDescriptor> = {
  category:      { key: 'category',      label: 'Category',       kind: FilterKind.General,  allowedTypes: ALL_TYPES },
  subcategory:   { key: 'subcategory',   label: 'Sub-category 1', kind: FilterKind.General,  allowedTypes: ALL_TYPES },
  subcategory2:  { key: 'subcategory2',  label: 'Sub-category 2', kind: FilterKind.General,  allowedTypes: ALL_TYPES },
  vendor:        { key: 'vendor',        label: 'Brand (Vendor)', kind: FilterKind.General,  allowedTypes: ALL_TYPES },
  priceRange:    { key: 'priceRange',    label: 'Price Range',    kind: FilterKind.General,  allowedTypes: ALL_TYPES },
  material:      { key: 'material',      label: 'Material',       kind: FilterKind.Specific, allowedTypes: [ItemType.Jewelery] },
  stoneName:     { key: 'stoneName',     label: 'Stone Name',     kind: FilterKind.Specific, allowedTypes: [ItemType.Jewelery] },
  gender:        { key: 'gender',        label: 'Gender',         kind: FilterKind.Specific, allowedTypes: [ItemType.Jewelery, ItemType.Watch] },
  movement:      { key: 'movement',      label: 'Movement',       kind: FilterKind.Specific, allowedTypes: [ItemType.Watch] },
  shape:         { key: 'shape',         label: 'Watch Shape',    kind: FilterKind.Specific, allowedTypes: [ItemType.Watch] },
  band:          { key: 'band',          label: 'Band',           kind: FilterKind.Specific, allowedTypes: [ItemType.Watch] },
  style:         { key: 'style',         label: 'Pattern',        kind: FilterKind.Specific, allowedTypes: [ItemType.Tableware, ItemType.Giftware] },
  patternNumber: { key: 'patternNumber', label: 'Pattern No.',    kind: FilterKind.Specific, allowedTypes: [ItemType.Tableware, ItemType.Giftware] },
}

export const FILTER_KEYS = Object.keys(FILTER_CATALOG) as ReadonlyArray<string>

/**
 * Filter keys whose rendering is still gated because the website doesn't render
 * them yet. Keep in lockstep with the website's `RENDER_DISABLED` set in
 * `gann-website-frontend/src/components/category/filter-registry/registry.tsx`.
 *
 * Admin UI uses this to disable "Add" in the override drawer and hide these
 * keys from the effective-preview, so authors don't ship configurations the
 * website won't honour.
 *
 * material/stoneName/movement/shape/band are now fully wired (the `productsFind`
 * resolver filters by these params, `productFilterFacets` serves their values,
 * and the website renders them) — so they were removed from this set. `style`
 * (surfaced as "Pattern") is now wired too: the website renders a Vendor→Pattern
 * dependent filter backed by `stylesFind`, and `productsFind` filters by `style`.
 * Only `patternNumber` remains ungated work.
 */
export const PHASE_4_DISABLED_FILTER_KEYS: ReadonlySet<string> = new Set([
  'patternNumber',
])

export function isPhase4Disabled(key: string): boolean {
  return PHASE_4_DISABLED_FILTER_KEYS.has(key)
}

export function getFilterDescriptor(key: string): FilterDescriptor | undefined {
  return FILTER_CATALOG[key]
}

export function isGeneralFilter(key: string): boolean {
  return FILTER_CATALOG[key]?.kind === FilterKind.General
}

export function isFilterAllowedForType(key: string, type: ItemType): boolean {
  return !!FILTER_CATALOG[key]?.allowedTypes.includes(type)
}

export const PRODUCT_TYPES_IN_UI: ReadonlyArray<ItemType> = [
  ItemType.Jewelery,
  ItemType.Watch,
  ItemType.Tableware,
  ItemType.Giftware,
]

export function productTypeDisplayLabel(type: ItemType): string {
  switch (type) {
    case ItemType.Jewelery:  return 'Jewelry'
    case ItemType.Watch:     return 'Watch'
    case ItemType.Tableware: return 'Tableware'
    case ItemType.Giftware:  return 'Giftware'
    case ItemType.Stone:     return 'Stone'
    default:                 return type
  }
}
